import React, { useState } from 'react';
import { BrowserRouter as Router, Switch, Redirect, Route, useLocation } from 'react-router-dom';
import ErrorPage from '../ui/Error';
import Dashboard from '../containers/Dashboard';
import Packages from '../containers/Packages';
import Hosts from '../containers/Hosts';
import HostOwnership from '../containers/HostOwnership';
import Vulnerabilities from '../containers/Vulnerabilities';
import Tickets from '../containers/Tickets';
import TicketDetail from '../containers/TicketDetail';
import CvePage from '../containers/CvePage';
import PackagePage from '../containers/PackagePage';
import HostPage from '../containers/HostPage';
import ContainerImages from '../containers/ContainerImages';
import ContainerImagePage from '../containers/ContainerImagePage';

import Investigation from '../containers/Investigation';
import OAuthPage from './OauthPage';
import AuthenticatedRoute from './AuthenticatedRoute';
import { useAuth } from '../utils/hooks';
import AppShell from '../ui/AppShell';
import { QueryProvider } from '../utils/QueryContext';
import { ThemeProvider } from '../utils/ThemeContext';
import GlobalLoadingIndicator from './GlobalLoadingIndicator';
import SchedulePanel from '../containers/SchedulePanel';
import { ToastProvider } from '../ui/Toast';
import LEGACY_REDIRECTS from '../ui/legacyRedirects';

// Must be registered before PAGE_ROUTES, whose paths also match their sub-paths.
const ENTITY_ROUTES = [
  { route: 'vulnerabilities/:cve', component: CvePage },
  { route: 'packages/:id', component: PackagePage },
  { route: 'hosts/:id', component: HostPage },
  { route: 'container-images/:id', component: ContainerImagePage },
];

const PAGE_ROUTES = [
  { route: 'dashboard', component: Dashboard },
  { route: 'packages', component: Packages },
  { route: 'hosts', component: Hosts },
  { route: 'container-images', component: ContainerImages },
  { route: 'vulnerabilities', component: Vulnerabilities },
  { route: 'tickets', component: Tickets },
  { route: 'query', component: Investigation },
  { route: 'host-ownership', component: HostOwnership },
];

function AppRoutes({ isAuthenticated, loading }) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const redirects = LEGACY_REDIRECTS.filter((redirect) => !redirect.when || redirect.when(query));

  return (
    <Switch>
      <Route path="/oauth">
        <OAuthPage />
      </Route>
      <Redirect exact from="/" to="/dashboard" />
      {redirects.map(({ path, exact, to }, i) => (
        <Route
          key={`redirect-${path}-${i}`}
          exact={exact}
          path={path}
          render={({ match }) => <Redirect to={to(match.params, query, location.search)} />}
        />
      ))}
      <AuthenticatedRoute
        path="/tickets/:ticketId"
        component={TicketDetail}
        isAuthenticated={isAuthenticated}
        loading={loading}
      />
      {ENTITY_ROUTES.map((item) => (
        <AuthenticatedRoute key={`route-${item.route}`} exact path={`/${item.route}`} component={item.component} isAuthenticated={isAuthenticated} loading={loading} />
      ))}
      {PAGE_ROUTES.map((item) => (
        <AuthenticatedRoute key={`route-${item.route}`} path={`/${item.route}`} component={item.component} isAuthenticated={isAuthenticated} loading={loading} />
      ))}
      <Route render={() => <ErrorPage statusCode={404} />} />
    </Switch>
  );
}

const App = () => {
  const { isAuthenticated, loading } = useAuth();
  const [schedulesOpen, setSchedulesOpen] = useState(false);

  const handleNavAction = (action) => {
    if (action === 'openCronSchedule') {
      setSchedulesOpen(true);
    }
  };

  const routes = <AppRoutes isAuthenticated={isAuthenticated} loading={loading} />;

  return (
    <ThemeProvider>
    <QueryProvider>
    <ToastProvider>
      <Router>
        <GlobalLoadingIndicator />
        {isAuthenticated && <SchedulePanel open={schedulesOpen} onClose={() => setSchedulesOpen(false)} />}
        {isAuthenticated ? (
          <AppShell onNavAction={handleNavAction}>{routes}</AppShell>
        ) : (
          <div className="h-screen overflow-hidden">{routes}</div>
        )}
      </Router>
    </ToastProvider>
    </QueryProvider>
    </ThemeProvider>
  );
};

export default App;
